/**
 * The bar that appears next to selected editor text (#279).
 *
 * Actions mode shows one icon per thing that can be made from the selection:
 * Task, Note, Excerpt (excerpt sources only, #277) and Format. Format swaps
 * the bar in place for the formatting controls; Task and Note swap it for a
 * title input pre-filled from the selection and reuse the toolbar's create
 * flow (createFromText.ts).
 *
 * Adding an action means adding an entry to `actions` - nothing else in the
 * bar needs to know about it.
 */

import { useCallback, useEffect, useMemo, useReducer, useRef, useState, type ReactNode } from 'react';
import type { Editor } from '@tiptap/react';
import { BubbleMenu } from '@tiptap/react/menus';
import { NodeSelection } from '@tiptap/pm/state';
import {
  ArrowLeft,
  Bold,
  Check,
  Code,
  Highlighter,
  Italic,
  Link,
  Palette,
  RemoveFormatting,
  Flag,
  Scissors,
  ListPlus,
  SquareCheckBig,
  StickyNote,
  Strikethrough,
  Type,
  Underline as UnderlineIcon,
  X,
} from 'lucide-react';
import { ColorPicker } from './ColorPicker';
import { activeColor, applyColor, clearFormatting } from './FormattingExtensions';
import { createEntityFromText, insertReference, referenceNode, type CreatableKind } from './createFromText';
import { truncateTitle } from './formatColors';

type Mode = 'actions' | 'format' | CreatableKind;

export interface SelectionAction {
  id: string;
  label: string;
  icon: ReactNode;
  /** Switch the bar to a mode, or run something and close. */
  run: () => void;
}

/** Present when the editor is an excerpt source (#277). */
export interface ExcerptBarHooks {
  /** Excerpt button / Ctrl+Shift+E: pick destinations for this range. */
  onExcerpt: (range: { from: number; to: number }) => void;
  /** Task/Note make an excerpt-backed item instead of replacing the text with a chip. */
  onCreateItem: (kind: CreatableKind, title: string, range: { from: number; to: number }) => Promise<void>;
  /** An excerpt range is being adjusted: offer "Set range" for the current selection. */
  adjusting: boolean;
  onApplyRange: () => void;
  onCancelAdjust: () => void;
}

interface SelectionBarProps {
  editor: Editor;
  meetingInstanceId?: string;
  scratchpadId?: string;
  onTaskCreated?: () => void;
  onNoteCreated?: () => void;
  excerpts?: ExcerptBarHooks;
  /** Flags (#316): opens the Flag menu for the selected line; absent where flags are off. */
  onFlag?: (anchor: { left: number; bottom: number }) => void;
  /** Named lists (#337): open the list picker for the selected lines; absent where lists are off. */
  onAddToList?: () => void;
}

/**
 * Whether the bar should be on screen. Kept pure so the rules are testable
 * without a browser: a real text selection in a focused, editable editor - or
 * the user is mid-way through using the bar (typing a title moves focus out
 * of the editor, and the bar must not vanish under them).
 */
export function shouldShowSelectionBar(p: {
  editable: boolean;
  empty: boolean;
  isNodeSelection: boolean;
  hasFocus: boolean;
  busy: boolean;
}): boolean {
  if (!p.editable) return false;
  if (p.busy) return true;
  if (p.empty || p.isNodeSelection) return false;
  return p.hasFocus;
}

/** Touch devices get the bar below the selection, out of the way of the native callout. */
function prefersBelow(): boolean {
  return typeof window !== 'undefined'
    && typeof window.matchMedia === 'function'
    && window.matchMedia('(pointer: coarse)').matches;
}

export function SelectionBar(props: SelectionBarProps) {
  const [mode, setMode] = useState<Mode>('actions');
  // shouldShow is captured by the plugin, so it reads the mode through a ref.
  const modeRef = useRef<Mode>('actions');
  useEffect(() => { modeRef.current = mode; }, [mode]);

  return (
    <BubbleMenu
      editor={props.editor}
      pluginKey="dossierSelectionBar"
      updateDelay={100}
      // The editor wrapper clips overflow, so the bar lives on <body>.
      appendTo={() => document.body}
      options={{ placement: prefersBelow() ? 'bottom' : 'top', offset: 8, flip: true, shift: { padding: 8 } }}
      shouldShow={({ editor: ed, state }) => shouldShowSelectionBar({
        editable: ed.isEditable,
        empty: state.selection.empty,
        isNodeSelection: state.selection instanceof NodeSelection,
        hasFocus: ed.view.hasFocus(),
        busy: modeRef.current === 'task' || modeRef.current === 'note',
      })}
      className="selection-bar z-50"
    >
      <SelectionBarPanel {...props} mode={mode} onModeChange={setMode} />
    </BubbleMenu>
  );
}

/**
 * The bar's contents. Separate from the floating wrapper so it can be
 * rendered and tested on its own; it owns its mode unless one is passed in.
 */
export function SelectionBarPanel({
  editor, meetingInstanceId, scratchpadId, onTaskCreated, onNoteCreated, excerpts, onFlag, onAddToList, mode: modeProp, onModeChange,
}: SelectionBarProps & { mode?: Mode; onModeChange?: (m: Mode) => void }) {
  const [ownMode, setOwnMode] = useState<Mode>('actions');
  const mode = modeProp ?? ownMode;
  const setMode = useCallback((m: Mode) => {
    if (onModeChange) onModeChange(m);
    else setOwnMode(m);
  }, [onModeChange]);
  const modeRef = useRef<Mode>(mode);
  useEffect(() => { modeRef.current = mode; }, [mode]);

  const reset = useCallback(() => setMode('actions'), [setMode]);

  // Collapse back to the action icons whenever the selection goes away.
  useEffect(() => {
    const onSelection = () => {
      if (editor.state.selection.empty && modeRef.current === 'format') setMode('actions');
    };
    editor.on('selectionUpdate', onSelection);
    return () => { editor.off('selectionUpdate', onSelection); };
  }, [editor, setMode]);

  const actions: SelectionAction[] = useMemo(() => {
    const list: SelectionAction[] = [];
    if (excerpts?.adjusting) {
      list.push({ id: 'apply-range', label: 'Set excerpt range to selection', icon: <Check size={16} />, run: excerpts.onApplyRange });
      list.push({ id: 'cancel-range', label: 'Stop adjusting range', icon: <X size={16} />, run: excerpts.onCancelAdjust });
    }
    // Same order as the toolbar's command row: Flag, Task, Note, Excerpt (#340).
    if (onFlag) {
      list.push({
        id: 'flag',
        label: 'Flag this line',
        icon: <Flag size={16} />,
        run: () => {
          let anchor = { left: 0, bottom: 0 };
          try {
            const c = editor.view.coordsAtPos(editor.state.selection.from);
            anchor = { left: c.left, bottom: c.bottom };
          } catch { /* no layout (tests): the menu clamps itself on screen */ }
          onFlag(anchor);
        },
      });
    }
    list.push(
      { id: 'task', label: 'Create task from selection', icon: <SquareCheckBig size={16} />, run: () => setMode('task') },
      { id: 'note', label: 'Create note from selection', icon: <StickyNote size={16} />, run: () => setMode('note') },
    );
    if (excerpts) {
      list.push({
        id: 'excerpt',
        label: 'Excerpt selection (Ctrl+Shift+E)',
        icon: <Scissors size={16} />,
        run: () => {
          const { from, to, empty } = editor.state.selection;
          if (!empty) excerpts.onExcerpt({ from, to });
        },
      });
    }
    if (onAddToList) list.push({ id: 'list', label: 'Add to list (Alt+L)', icon: <ListPlus size={16} />, run: onAddToList });
    list.push({ id: 'format', label: 'Format', icon: <Type size={16} />, run: () => setMode('format') });
    return list;
  }, [setMode, excerpts, editor, onFlag, onAddToList]);

  return (
    <div
      role="toolbar"
      aria-label="Selection actions"
      className="flex items-center gap-0.5 px-1 py-1 rounded-lg border border-border bg-bg-card shadow-lg"
      // Keep the editor selection when clicking the bar's buttons.
      onMouseDown={(e) => {
        if ((e.target as HTMLElement).closest('input, textarea')) return;
        e.preventDefault();
      }}
    >
      {mode === 'actions' && actions.map((a) => (
        <BarButton key={a.id} title={a.label} onClick={a.run}>{a.icon}</BarButton>
      ))}
      {mode === 'format' && <FormatControls editor={editor} onBack={reset} />}
      {(mode === 'task' || mode === 'note') && (
        <CreateFromSelection
          key={mode}
          kind={mode}
          editor={editor}
          context={{ meetingInstanceId, scratchpadId }}
          onCreateItem={excerpts?.onCreateItem}
          onDone={() => {
            reset();
            (mode === 'task' ? onTaskCreated : onNoteCreated)?.();
          }}
          onCancel={() => { reset(); editor.commands.focus(); }}
        />
      )}
    </div>
  );
}

function BarButton({ title, onClick, isActive, disabled, children }: {
  title: string;
  onClick: () => void;
  isActive?: boolean;
  disabled?: boolean;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      title={title}
      aria-label={title}
      aria-pressed={isActive}
      disabled={disabled}
      onClick={onClick}
      className={`p-1.5 rounded-md transition-colors duration-100 disabled:opacity-30 disabled:cursor-not-allowed
        ${isActive ? 'bg-accent/20 text-accent-hover' : 'text-text-secondary hover:text-text-primary hover:bg-bg-hover'}`}
    >
      {children}
    </button>
  );
}

/** Re-render on every editor transaction so active states stay current. */
export function useEditorRerender(editor: Editor | null): void {
  const [, force] = useReducer((x: number) => x + 1, 0);
  useEffect(() => {
    if (!editor) return;
    editor.on('transaction', force);
    return () => { editor.off('transaction', force); };
  }, [editor]);
}

export function FormatControls({ editor, onBack }: { editor: Editor; onBack: () => void }) {
  useEditorRerender(editor);
  const [open, setOpen] = useState<null | 'highlight' | 'text' | 'link'>(null);
  const [url, setUrl] = useState('');
  const toggle = (p: 'highlight' | 'text' | 'link') => setOpen((cur) => (cur === p ? null : p));

  const applyLink = () => {
    const trimmed = url.trim();
    if (trimmed === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
    } else {
      const href = /^https?:\/\//.test(trimmed) ? trimmed : `https://${trimmed}`;
      editor.chain().focus().extendMarkRange('link').setLink({ href }).run();
    }
    setOpen(null);
    setUrl('');
  };

  return (
    <div className="relative flex items-center gap-0.5">
      <BarButton title="Back" onClick={onBack}><ArrowLeft size={16} /></BarButton>
      <div className="w-px h-4 bg-border mx-0.5" />
      <BarButton title="Bold (Ctrl+B)" isActive={editor.isActive('bold')} onClick={() => editor.chain().focus().toggleBold().run()}><Bold size={16} /></BarButton>
      <BarButton title="Italic (Ctrl+I)" isActive={editor.isActive('italic')} onClick={() => editor.chain().focus().toggleItalic().run()}><Italic size={16} /></BarButton>
      <BarButton title="Underline (Ctrl+U)" isActive={editor.isActive('underline')} onClick={() => editor.chain().focus().toggleUnderline().run()}><UnderlineIcon size={16} /></BarButton>
      <BarButton title="Strikethrough" isActive={editor.isActive('strike')} onClick={() => editor.chain().focus().toggleStrike().run()}><Strikethrough size={16} /></BarButton>
      <BarButton title="Inline code" isActive={editor.isActive('code')} onClick={() => editor.chain().focus().toggleCode().run()}><Code size={16} /></BarButton>
      <BarButton title="Highlight" isActive={editor.isActive('highlight')} onClick={() => toggle('highlight')}><Highlighter size={16} /></BarButton>
      <BarButton title="Text colour" isActive={!!activeColor(editor, 'text')} onClick={() => toggle('text')}><Palette size={16} /></BarButton>
      <BarButton title="Link" isActive={editor.isActive('link')} onClick={() => { setUrl(editor.getAttributes('link').href || ''); toggle('link'); }}><Link size={16} /></BarButton>
      <BarButton title="Clear formatting" onClick={() => clearFormatting(editor)}><RemoveFormatting size={16} /></BarButton>

      {(open === 'highlight' || open === 'text') && (
        <div className="absolute top-full left-0 mt-2 z-50 bg-bg-card border border-border rounded-lg shadow-lg">
          <ColorPicker
            kind={open}
            active={activeColor(editor, open === 'highlight' ? 'highlight' : 'text')}
            onPick={(c) => { applyColor(editor, open, c); setOpen(null); }}
          />
        </div>
      )}
      {open === 'link' && (
        <div className="absolute top-full left-0 mt-2 z-50 bg-bg-card border border-border rounded-lg shadow-lg p-2 flex items-center gap-1.5 min-w-[260px]">
          <input
            type="url"
            autoFocus
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') { e.preventDefault(); applyLink(); }
              if (e.key === 'Escape') { setOpen(null); editor.commands.focus(); }
            }}
            placeholder="https://example.com"
            className="flex-1 px-2 py-1 text-sm rounded-md bg-bg-input border border-border text-text-primary placeholder:text-text-muted focus:outline-none focus:border-accent"
          />
          <button type="button" onClick={applyLink} className="px-2 py-1 text-sm rounded-md bg-accent text-white hover:bg-accent-hover">Apply</button>
        </div>
      )}
    </div>
  );
}

function CreateFromSelection({ kind, editor, context, onCreateItem, onDone, onCancel }: {
  kind: CreatableKind;
  editor: Editor;
  context: { meetingInstanceId?: string; scratchpadId?: string };
  /** Excerpt sources (#277): the text stays and becomes a live excerpt in the new item. */
  onCreateItem?: ExcerptBarHooks['onCreateItem'];
  onDone: () => void;
  onCancel: () => void;
}) {
  // Captured now: once the title input has focus the selection is no longer
  // the one the user made.
  const [range] = useState(() => {
    const { from, to, empty } = editor.state.selection;
    return empty ? undefined : { from, to };
  });
  const [title, setTitle] = useState(() =>
    range ? truncateTitle(editor.state.doc.textBetween(range.from, range.to, ' ')) : '');
  const [replace, setReplace] = useState(true);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  useEffect(() => { requestAnimationFrame(() => inputRef.current?.focus()); }, []);

  const submit = async () => {
    const t = title.trim();
    if (!t || busy) return;
    setBusy(true);
    try {
      if (onCreateItem && range) {
        await onCreateItem(kind, t, range);
      } else {
        const created = await createEntityFromText(kind, t, context);
        insertReference(editor, referenceNode(kind, created), range, replace);
      }
      onDone();
    } catch (err) {
      console.error(`Failed to create ${kind}:`, err);
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-col gap-1 p-1 min-w-[280px]">
      <div className="flex items-center gap-1.5">
        {kind === 'task' ? <SquareCheckBig size={14} className="text-text-muted shrink-0" /> : <StickyNote size={14} className="text-text-muted shrink-0" />}
        <input
          ref={inputRef}
          type="text"
          aria-label={kind === 'task' ? 'Task title' : 'Note title'}
          value={title}
          disabled={busy}
          onChange={(e) => setTitle(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') { e.preventDefault(); submit(); }
            if (e.key === 'Escape') { e.preventDefault(); onCancel(); }
          }}
          placeholder={kind === 'task' ? 'Task title...' : 'Note title...'}
          className="flex-1 px-2 py-1 text-sm rounded-md bg-bg-input border border-border text-text-primary placeholder:text-text-muted focus:outline-none focus:border-accent disabled:opacity-50"
        />
        <button
          type="button"
          onClick={submit}
          disabled={busy || !title.trim()}
          className="px-2 py-1 text-sm rounded-md bg-accent text-white hover:bg-accent-hover disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {busy ? '...' : 'Create'}
        </button>
        <BarButton title="Back" onClick={onCancel}><ArrowLeft size={14} /></BarButton>
      </div>
      {range && !onCreateItem && (
        <label className="flex items-center gap-1.5 text-xs text-text-muted cursor-pointer select-none px-0.5">
          <input
            type="checkbox"
            checked={replace}
            disabled={busy}
            onChange={(e) => setReplace(e.target.checked)}
            className="accent-accent"
          />
          Replace selected text with a link
        </label>
      )}
    </div>
  );
}
